import React, { useEffect, useState } from 'react';
import ModuleTopExtras from '../ModuleTopExtras';
import { useLanguage } from '../../i18n/LanguageContext';
import ListeningUpload from './listening/ListeningUpload';
import ListeningPractice from './listening/ListeningPractice';
import ListeningScore from './listening/ListeningScore';
import { ListeningSet } from '../../config/api';
import './Modules.css';

const ListeningModule: React.FC = () => {
  const { t } = useLanguage();
  const [stage, setStage] = useState<'upload' | 'practice' | 'score'>('upload');
  const [listeningSet, setListeningSet] = useState<ListeningSet | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [audioName, setAudioName] = useState<string>('listening-practice');
  const [score, setScore] = useState<any>(null);
  const [results, setResults] = useState<any[]>([]);

  // Revoke the in-browser audio object URL when it changes / unmounts.
  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  const handleUploaded = (set: ListeningSet, file: File) => {
    setListeningSet(set);
    setAudioUrl(URL.createObjectURL(file));
    setAudioName(file.name);
    setStage('practice');
  };

  const handleSubmitScore = (s: any, r: any[]) => {
    setScore(s);
    setResults(r);
    setStage('score');
  };

  // Retake: back to the question page with the same audio/questions.
  // ListeningPractice remounts, so answers start blank for a fresh attempt.
  const handleRetry = () => {
    setScore(null);
    setResults([]);
    setStage('practice');
  };

  const handleBack = () => {
    setStage('upload');
    setListeningSet(null);
    setScore(null);
    setResults([]);
    setAudioName('listening-practice');
    setAudioUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const renderStage = () => {
    if (stage === 'upload') {
      return <ListeningUpload onUploaded={handleUploaded} />;
    }
    if (stage === 'practice' && listeningSet) {
      return (
        <ListeningPractice
          listeningSet={listeningSet}
          audioUrl={audioUrl}
          audioName={audioName}
          onSubmitScore={handleSubmitScore}
          onBack={handleBack}
        />
      );
    }
    if (stage === 'score' && listeningSet && score) {
      return (
        <ListeningScore
          score={score}
          results={results}
          transcript={listeningSet.transcript}
          audioUrl={audioUrl}
          audioName={audioName}
          questions={listeningSet.questions}
          onBack={handleBack}
          onRetry={handleRetry}
        />
      );
    }
    return null;
  };

  return (
    <div className="module-container listening-module">
      <header className="module-header">
        <span className="module-icon">🎧</span>
        <h1 className="module-title">{t('moduleHeaders.listening.title')}</h1>
        <p className="module-subtitle">{t('moduleHeaders.listening.subtitle')}</p>
      </header>
      <ModuleTopExtras moduleId="listening" />
      <section className="module-content">
        {renderStage()}
      </section>
    </div>
  );
};

export default ListeningModule;
